import React, { useEffect, useMemo, useRef, useState } from 'react'
import { Button, Result, Typography, Collapse, List, Avatar, Badge, Tag, Timeline, Tooltip, Space, Card, Row, Col, Modal, Spin, Divider, Progress, Statistic } from "antd";
import { useNavigate } from 'react-router-dom'
import { emitEvent, EventName, useEventBus } from '@/hooks/useEventBus'
import { CertifyStatus } from '../UtilityData/CertifyStatus';
import { useMount, useRequest } from 'ahooks'
import { api } from '@/redux/api'
import { CheckCircleFilled, ClockCircleFilled, UploadOutlined, UserOutlined, InfoCircleFilled, ArrowRightOutlined, ThunderboltOutlined, FireOutlined, DashboardOutlined, CalendarOutlined } from '@ant-design/icons';
import { useFacilityId } from '@/hooks/use-facilityid'
import UpdateEUI from './UpdateEUI'
import moment from 'moment'
import { useKeepAliveContext } from '@/hooks/use-keep-alive-context'
import { CertifyWrapper } from './Certify.style';
import { useSelector } from 'react-redux';
import { buildFacilityPath, getFacilityContext } from '@/utils/route-context';

const { Text, Title } = Typography;
const { Item: TimelineItem } = Timeline;

const statusColors = {
  [CertifyStatus.Certified]: '#52c41a',  // 绿色
  [CertifyStatus.PendingCertification]: '#faad14',  // 黄色
  [CertifyStatus.Uncertified]: '#8c8c8c',  // 灰色
  [CertifyStatus.Rejected]: '#ff7875'  // 淡红色
}


// 日志活动类型对应的图标和颜色
const getActivityIcon = (action) => {
  if (action.includes('Completed') || action.includes('completed')) {
    return <CheckCircleFilled style={{ color: '#52c41a', fontSize: '18px' }} />;
  } else if (action.includes('Update') || action.includes('updated')) {
    return <InfoCircleFilled style={{ color: '#1890ff', fontSize: '18px' }} />;
  } else if (action.includes('submitted')) {
    return <UploadOutlined style={{ color: '#722ed1', fontSize: '18px' }} />;
  } else {
    return <ClockCircleFilled style={{ color: '#faad14', fontSize: '18px' }} />;
  }
};

// 状态标签颜色映射（五态流程：Submitted/Assigned/Accepted/Rejected/Completed）
const statusColorMap = {
  'Submitted': 'blue',
  'Assigned': 'purple',
  'Accepted': 'cyan',
  'Rejected': 'red',
  'Completed': 'green'
};

// 日志列表组件
const CertificationLogList = ({ logs = [] }) => {
  if (!logs || logs.length === 0) {
    return (
      <div style={{ textAlign: 'center', padding: '40px', color: '#8c8c8c' }}>
        <InfoCircleFilled style={{ fontSize: '48px', marginBottom: '16px', color: '#d9d9d9' }} />
        <div>No certification logs available</div>
      </div>
    );
  }

  return (
    <div className="timeline-container">
      <Timeline mode="left">
        {logs.map(log => (
          <TimelineItem
            key={log.id}
            color={statusColorMap[log.status] || 'blue'}
            dot={getActivityIcon(log.action)}
          >
            <div className="log-item">
              <div className="log-header">
                <div className="log-user-info">
                  {log.operator_details ? (
                    <Tooltip title={log.operator_details.user_name}>
                      <Avatar
                        src={log.operator_details.user_icon}
                        size={36}
                        icon={<UserOutlined />}
                      />
                    </Tooltip>
                  ) : (
                    <Avatar size={36} icon={<UserOutlined />} style={{ backgroundColor: '#87d068' }} />
                  )}
                  <Text className="log-action">{log.action}</Text>
                  <Tag color={statusColorMap[log.status] || 'default'}>{log.status}</Tag>
                </div>
                <Text className="log-date">
                  {new Date(log.created_date).toLocaleString()}
                </Text>
              </div>

              <div className="log-content">
                <div className="log-information">{log.information}</div>
                {log.reason && (
                  <div className="log-reason">
                    <strong>Reason:</strong> {log.reason}
                  </div>
                )}
              </div>
            </div>
          </TimelineItem>
        ))}
      </Timeline>
    </div>
  );
};

// 创建一个用户信息卡片组件
const UserInfoCard = ({ title, user }) => {
  if (!user) return null;

  return (
    <div className="user-card">
      <div className="user-avatar">
        <Tooltip title={user.user_name}>
          <Avatar
            src={user.user_icon}
            icon={<UserOutlined />}
            size={80}
          />
        </Tooltip>
      </div>
      <Title level={5} className="user-name">{user.user_name}</Title>
      <Text className="user-role">{title}</Text>
    </div>
  );
};

// 创建自定义步骤条组件
const CustomSteps = ({ steps, current }) => {
  const getStepStatus = (index) => {
    if (index < current) return 'completed';
    if (index === current) return 'active';
    return 'pending';
  };

  return (
    <div className="custom-steps-container">
      <div className="custom-steps">
        {steps.map((step, index) => (
          <div key={index} className={`step-item ${getStepStatus(index)}`}>
            <div className={`step-circle ${getStepStatus(index)}`}>
              <span className="step-number">{index + 1}</span>
              <CheckCircleFilled className="step-check" />
            </div>
            <div className="step-title">{step.title}</div>
          </div>
        ))}
      </div>
    </div>
  );
};

// 创建统计卡片组件
const StatCard = ({ type, data, onClick }) => {
  if (!data) return null;

  const { total, certified } = data;
  const uncertified = total - certified;
  const percentage = total > 0 ? Math.round((certified / total) * 100) : 0;

  const getIcon = () => {
    switch (type) {
      case 'electricity': return <ThunderboltOutlined />;
      case 'gas': return <FireOutlined />;
      case 'overall': return <DashboardOutlined />;
      default: return <DashboardOutlined />;
    }
  };

  const getTitle = () => {
    switch (type) {
      case 'electricity': return 'Electricity Certification';
      case 'gas': return 'Gas Certification';
      case 'overall': return 'Overall Status';
      default: return 'Status';
    }
  };

  const getStatus = () => {
    if (certified === total && total > 0) return 'completed';
    if (certified > 0) return 'partial';
    return 'pending';
  };

  const getStatusText = () => {
    const status = getStatus();
    switch (status) {
      case 'completed': return 'Completed';
      case 'partial': return 'In Progress';
      case 'pending': return 'Pending';
      default: return 'Unknown';
    }
  };

  const getProgressColor = () => {
    if (percentage === 100) return '#52c41a';
    if (percentage > 0) return '#faad14';
    return '#8c8c8c';
  };

  return (
    <div className={`stat-card ${type}`} onClick={() => onClick && onClick(type)}>
      <div className="stat-header">
        <div className="stat-title">
          <div className={`stat-icon ${type}`}>
            {getIcon()}
          </div>
          <h3 className="stat-name">{getTitle()}</h3>
        </div>

      </div>

      <div className="stat-content">
        <div className="stat-numbers">
          <div className="main-stats">
            <span className="certified-count">{certified}</span>
            <span className="separator">/</span>
            <span className="total-count">{total}</span>
          </div>
          <div className="stat-labels">
            <div className="stat-label">
              <div className="label-dot certified"></div>
              Certified: {certified}
            </div>
            <div className="stat-label">
              <div className="label-dot uncertified"></div>
              Pending: {uncertified}
            </div>
          </div>
        </div>

        <div className="stat-progress">
          <Progress
            type="circle"
            percent={percentage}
            size={80}
            strokeColor={getProgressColor()}
            strokeWidth={8}
            format={(percent) => `${percent}%`}
          />
        </div>
      </div>

      <div className="stat-footer">
        <div className="percentage-text">
          {percentage}% Certification Complete
        </div>
        {onClick && (
          <div className="view-details">
            View Details <ArrowRightOutlined />
          </div>
        )}
      </div>
    </div>
  );
};

const Certify = () => {
  const [currentStep, setCurrentStep] = useState(0);
  const navigate = useNavigate();
  const facility_id = useFacilityId()
  const { refreshTab } = useKeepAliveContext()
  const facilityContext = useSelector((state) => getFacilityContext(state))

  const { data: certificationData = {
    electricity: { total: 0, certified: 0, percentage: 0 },
    gas: { total: 0, certified: 0, percentage: 0 },
    overall: { total: 0, certified: 0, percentage: 0 }
  }, loading: certificationDataLoading, run: runCertificationData } = useRequest(() => api.getEUICertificationStatus(), {
    manual: true
  });

  const { data: certifyInfo = {}, loading: certifyInfoLoading, run: runCertifyInfo } = useRequest(async () => {
    const res = await api.getCertifyRequestsById()
    if (res.id) {
      return api.getCertifyLogs(res.id)
    }
  }, {
    manual: true
  })

  const isLoading = certifyInfoLoading || certificationDataLoading

  const initStep = (certifyInfo, certificationData) => {
    if (!certifyInfo?.task) {
      return
    }
    const isAllDataCertified = certificationData.electricity.certified === certificationData.electricity.total && certificationData.gas.certified === certificationData.gas.total

    // 如果任务已完成
    if (certifyInfo?.task?.status === 'Completed') {
      if (isAllDataCertified) {
        setCurrentStep(4) // Completed
      } else {
        setCurrentStep(0) // 重新开始
      }
    } 
    // 如果任务已提交但还没有分配给 Assignee
    else if (certifyInfo?.task?.status === 'Submitted' && !certifyInfo?.task?.certify_by?.user_id) {
      setCurrentStep(1) // Pending Assign
    }
    // 任务正在进行中
    else {
      if (isAllDataCertified) {
        setCurrentStep(3) // Update EUI
      } else {
        setCurrentStep(2) // Pending Certification
      }
    }
  }

  useEffect(() => {
    initStep(certifyInfo, certificationData)
  }, [certifyInfo, certificationData])

  const fetchData = () => {
    runCertificationData()
    runCertifyInfo()
  }

  useMount(() => {
    fetchData()
  })

  useEventBus(EventName.UPDATE_CERTIFICATION_STATUS, () => {
    fetchData()
  })

  const updateEUI = (euidata) => {
    Modal.confirm({
      title: 'Confirm Update',
      content: 'Are you sure you want to update the EUI data?',
      okText: 'Confirm',
      cancelText: 'Cancel',
      onOk: () => {
        api.updateBenchmarkEUI(facility_id, euidata, certifyInfo.task.id).then(res => {
          if (res.status === 200) {
            fetchData()
            emitEvent(EventName.REFRESH_CERTIFY_REQUEST)
          }
        })
      }
    });
  }

  const requestCertification = () => {
    api.requestEUITask(facility_id).then(() => {
      refreshTab()
      emitEvent(EventName.REFRESH_CERTIFY_REQUEST)
    })
  }

  const goToPage = (pageType, query) => {
    let baseUrl = ''
    if (pageType === 'elec') {
      emitEvent(EventName.CLOSE_ELECTRICITY)
      baseUrl = buildFacilityPath('electricity', facilityContext) || '/switch-view'
    } else if (pageType === 'gas') {
      emitEvent(EventName.CLOSE_GAS)
      baseUrl = buildFacilityPath('gas', facilityContext) || '/switch-view'
    }

    if (query) {
      const searchParams = new URLSearchParams()
      for (const key in query) {
        searchParams.append(key, query[key])
      }
      navigate(baseUrl + '?' + searchParams.toString());
    } else {
      navigate(baseUrl);
    }
  }



  const steps = [
    {
      title: 'Request Certification'
    },
    {
      title: 'Pending Assign'
    },
    {
      title: 'Pending Certification'
    },
    {
      title: 'Update EUI'
    },
    {
      title: 'Completed'
    },
  ];

  const onStatCardClick = (type) => {
    if (type === 'gas') {
      goToPage('gas');
    } else if (type === 'electricity') {
      goToPage('elec');
    }
  }

  const getStepContent = (step) => {
    switch (step) {
      case 0:
        return (
          <div>
            <Title level={4} className="step-title">
              Ready to request data certification?
            </Title>
            <div className="step-description">
              Click the button below to initiate the certification process for your utility data.
            </div>
            <div className="step-actions">
              <Button
                type="primary"
                size="large"
                onClick={requestCertification}
                icon={<ArrowRightOutlined />}
              >
                Request Certification
              </Button>
            </div>
          </div>
        );
      case 1:
        return (
          <div>
            <Title level={4} className="step-title">
              Waiting for task assignment
            </Title>
            <div className="step-description">
              Your certification request has been submitted successfully. The task is now pending assignment by an administrator. You will be notified once an assignee has been designated to handle your request.
            </div>
          </div>
        );
      case 2:
        return (
          <div>
            <Title level={4} className="step-title">
              Certify your pending data
            </Title>
            <div className="step-description">
              You have {certificationData.electricity.total - certificationData.electricity.certified} Electricity and {certificationData.gas.total - certificationData.gas.certified} Gas data entries pending certification. Please visit the respective pages to certify them.
            </div>
            <div className="step-actions">

              <Button
                type="primary"
                size="large"
                onClick={() => goToPage('elec')}
                icon={<ArrowRightOutlined />}
              >
                Go to Electricity Page
              </Button>
              <Button
                type="primary"
                size="large"
                onClick={() => goToPage('gas')}
                icon={<ArrowRightOutlined />}
              >
                Go to Gas Page
              </Button>
            </div>
          </div>
        );
      case 3:
        return (
          <div>
            <Title level={4} className="step-title">
              {/* {benchmarkData.eui_elec} */}
            </Title>
            <UpdateEUI updateEUI={updateEUI} task={certifyInfo.task} />
          </div>
        );
      case 4:
        return (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
            <Result
              className="success-result"
              status="success"
              title="Certification Complete!"
              subTitle="All data has been certified and EUI data is current. Your facility is now fully compliant and no further action is needed at this time."
            />
            <Button 
              type="primary" 
              size="large"
              onClick={() => {
                Modal.confirm({
                  title: "Recertify?",
                  content: "All data has been certified and EUI data is current. Do you still want to recertify?",
                  okText: "Yes",
                  cancelText: "No",
                  onOk: requestCertification,
                });
              }}
            >
              Recertify
            </Button>
          </div>
        );
      default:
        return null;
    }
  };

  const getStatusInfo = () => {
    let status = certifyInfo.task?.status
    let statusType = 'completed'

    if (currentStep === 0) {
      status = 'Pending Request'
      statusType = 'pending'
    } else if (currentStep === 1) {
      status = 'Pending Assignment'
      statusType = 'partial'
    } else if (currentStep === 2 || currentStep === 3) {
      statusType = 'partial'
    }

    return { status, statusType }
  }

  const getStatusIcon = (statusType) => {
    switch (statusType) {
      case 'completed': return <CheckCircleFilled />;
      case 'partial': return <ClockCircleFilled />;
      case 'pending': return <InfoCircleFilled />;
      default: return <InfoCircleFilled />;
    }
  }

  return (
    <Spin spinning={isLoading} tip="Loading certification data..." size="large">
      <CertifyWrapper>
        <div className="main-container">
          {/* Page Header */}
          <div className="page-header">
            <div className="header-content">
              <div className="header-left">
                <Title level={1} className="header-title">
                  Facility Data Certification
                </Title>
                <div className="header-subtitle">
                  Manage and track your utility data certification process
                </div>
              </div>

              {certifyInfo.task && (
                <div className="header-right">
                  <div className="status-display">
                    <div className={`status-icon ${getStatusInfo().statusType}`}>
                      {getStatusIcon(getStatusInfo().statusType)}
                    </div>
                    <div className="status-info">
                      <div className="status-label">Current Status</div>
                      <div className="status-value">{getStatusInfo().status}</div>
                    </div>
                  </div>

                  {certifyInfo.task.modified_date && (
                    <div className="modified-date-display">
                      <CalendarOutlined className="date-icon" />
                      Last modified: {moment(certifyInfo.task.modified_date).format('MMM DD, YYYY')}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Statistics Section */}
          <div className="stats-section">
            <StatCard
              type="electricity"
              data={certificationData.electricity}
              onClick={onStatCardClick}
            />
            <StatCard
              type="gas"
              data={certificationData.gas}
              onClick={onStatCardClick}
            />
            <StatCard
              type="overall"
              data={certificationData.overall}
            />
          </div>



          {/* Progress Section */}
          <div className="progress-section">
            <CustomSteps steps={steps} current={currentStep} />
            <div className="step-content">
              {getStepContent(currentStep)}
            </div>
          </div>

          {/* Users Section */}
          {certifyInfo.task && (
            <div className="users-section">
              <Title level={3} style={{ textAlign: 'center', marginBottom: '32px', color: '#2c3e50' }}>
                Certification Team
              </Title>
              <div className="users-grid">
                <UserInfoCard title="Requested By" user={certifyInfo.task.request_by} />
                <UserInfoCard title="Assigned By" user={certifyInfo.task.received_by} />
                <UserInfoCard title="Assigned To" user={certifyInfo.task.certify_by} />
              </div>
            </div>
          )}

          {/* History Section */}
          <div className="history-section">
            <Collapse
              defaultActiveKey={['1']}
              ghost
            >
              <Collapse.Panel
                key="1"
                header={
                  <Title level={4} style={{ margin: 0, color: '#2c3e50' }}>
                    Certification History & Logs
                  </Title>
                }
              >
                {currentStep === 0 ? (
                  <div style={{ textAlign: 'center', padding: '40px', color: '#8c8c8c' }}>
                    <InfoCircleFilled style={{ fontSize: '48px', marginBottom: '16px', color: '#d9d9d9' }} />
                    <div>No certification logs available yet. Start by requesting certification.</div>
                  </div>
                ) : (
                  <CertificationLogList logs={certifyInfo.logs} />
                )}
              </Collapse.Panel>
            </Collapse>
          </div>
        </div>
      </CertifyWrapper>
    </Spin>
  )
}

export default Certify
